import {
  Box,
  Button,
  makeStyles,
  Popover,
  Typography,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FilterBox from 'src/app/components/FilterBox';
import { PAGE_SIZE_LIST } from 'src/app/constant/config';
import {
  getListDevice,
  importTransferDevice,
  resetResultImport
} from 'src/features/deviceSlice';
import CreateDevice from './CreateDevice';
import { getListDeviceVersion } from 'src/features/versionDeviceSlice';
import SearchInput from '../../components/input/SearchInput';

const Toolbar = ({ searchRef, isLoading, listAgencies }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isAccountant = useSelector(state => state.authSlice.isAccountant);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const hasManagePermission = isAdmin || isAgency;
  const resultTransferImport = useSelector(state => state.deviceSlice.resultTransferImport);

  useEffect(() => {
    if (!resultTransferImport) return;
    const blob = new Blob([resultTransferImport]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_import_transfer_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);

    dispatch(resetResultImport(resetResultImport));
  }, [resultTransferImport]);

  const fileInput = useRef(null);

  const [query, setQuery] = useState({
    agency_id: undefined,
    device_type: undefined,
    status: undefined,
    sub: undefined,
    keywords: undefined,
    page: 1,
    page_size: PAGE_SIZE_LIST
  });

  const getListDeviceVersionWithParams = useCallback(
    data => {
      dispatch(getListDeviceVersion(data));
    },
    [dispatch]
  );

  // Memoize handlers để tối ưu hiệu năng
  const handleSearchChange = useCallback(value => {
    setQuery(prevQuery => ({
      ...prevQuery,
      keywords: value || undefined,
      page: 1
    }));
  }, []);

  const handleSearch = useCallback(
    value => {
      setQuery(prevQuery => {
        const newQuery = {
          ...prevQuery,
          keywords: value || undefined,
          page: 1
        };
        if (searchRef) {
          searchRef(newQuery);
        }
        return newQuery;
      });
    },
    [searchRef]
  );

  const handleClickFilter = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const open = Boolean(anchorEl);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setOpenCreateModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpenCreateModal(false);
  }, []);

  const upload = useCallback(
    file => {
      if (file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        dispatch(importTransferDevice(formData));
        document.getElementById('importTransferDevice').value = '';
        dispatch(getListDevice(query));
      }
    },
    [dispatch, query]
  );

  const onFileChange = useCallback(
    event => {
      upload(event.target.files[0]);
    },
    [upload]
  );

  return (
    <div className={classes.root}>
      {openCreateModal && (
        <CreateDevice
          open={openCreateModal}
          onClose={closeModal}
          getListVersion={getListDeviceVersionWithParams}
        />
      )}
      <Box>
        <Box className={classes.groupSearch}>
          <div className="col-12 col-md-3">
            <Typography className={classes.title}>Quản trị thiết bị</Typography>
          </div>
          <div className="col-12 col-md-9 d-flex justify-content-end">
            <SearchInput
              value={query.keywords || ''}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              className={'searchInputDevice'}
              placeholder="Serial hoặc biển số"
            />
            {(isAdmin || isAgency || isAccountant || isSale || isTechnician) && (
              <Typography
                color="secondary"
                size="small"
                variant="contained"
                className="pt-2 mx-3 cursor-pointer"
              >
                <span
                  className="cursor-pointer"
                  aria-describedby="filter-module"
                  onClick={handleClickFilter}
                  style={{ fontSize: '15px', fontWeight: 'bold' }}
                >
                  <img
                    src={`/static/iconSvg/filter.svg`}
                    style={{ paddingRight: '5px', paddingBottom: '4px' }}
                  />
                  &nbsp; Lọc
                </span>

                <Popover
                  id="filter-module"
                  open={open}
                  keepMounted
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                >
                  <FilterBox
                    listAgencies={listAgencies}
                    searchRef={searchRef}
                    isAdmin={isAdmin}
                    hasManagePermission={hasManagePermission}
                  />
                </Popover>
              </Typography>
            )}

            {(isAdmin || isAgency || isAccountant || isSale || isTechnician) && (
              <>
                <input
                  name="file"
                  ref={fileInput}
                  id="importTransferDevice"
                  onChange={onFileChange}
                  type="file"
                  hidden
                />
                <Typography
                  onClick={() => fileInput.current.click()}
                  color="secondary"
                  size="small"
                  variant="contained"
                  className="pt-2 mx-3 cursor-pointer"
                >
                  <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    <img
                      src={`/static/iconSvg/import.svg`}
                      style={{ paddingRight: '5px', paddingBottom: '4px' }}
                    />
                    &nbsp; {isTechnician ? 'Nhập kho từ file' : 'Chuyển kho từ file'}
                  </span>
                </Typography>
                {(isAdmin || isAccountant || isSale || isTechnician) && (
                  <Button
                    onClick={handleOpenCreateModal}
                    style={{
                      ...(isMobile && { lineHeight: '18px' }),
                      marginLeft: '10px',
                      color: '#ffff',
                      height: '40px',
                      textTransform: 'none'
                    }}
                    color="secondary"
                    variant="contained"
                  >
                    {isMobile ? 'Thêm thiết bị' : 'Thêm mới thiết bị'}
                  </Button>
                )}
              </>
            )}
          </div>
        </Box>
      </Box>
    </div>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

const useStyles = makeStyles(theme => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#0C1132',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      fontWeight: '500'
    }
  },
  groupSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // overflowX: 'scroll',
    minWidth: '50%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      gap: '8px'
    }
  },
  groupSearchLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  searchInput: {
    width: '353px'
  },
  exportBtn: {
    justifyContent: 'flex-end',
    display: 'flex',
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  shadowBox: {
    boxShadow: '0 2px 5px rgba(0,0,0,.18)'
  }
}));

export default Toolbar;
